/**
 * Newsletter Feature Constants
 * All Greek text and configuration for the newsletter system
 */

import type { CategoryConfig, FooterNewsletterCategory, NewsletterFormData } from './types';

/** Newsletter subscription categories */
export const NEWSLETTER_CATEGORIES: CategoryConfig[] = [
  {
    id: 'tvs',
    label: 'TVs',
    description: 'Τηλεοράσεις και οθόνες',
    icon: 'tv',
  },
  {
    id: 'smartphones',
    label: 'SmartPhones',
    description: 'Κινητά τηλέφωνα',
    icon: 'smartphone',
  },
  {
    id: 'tablets',
    label: 'Tablets',
    description: 'Tablets και αξεσουάρ',
    icon: 'tablet',
  },
  {
    id: 'gaming',
    label: 'Gaming',
    description: 'Κονσόλες και gaming εξοπλισμός',
    icon: 'gaming',
  },
  {
    id: 'laptops',
    label: 'Laptops',
    description: 'Φορητοί υπολογιστές',
    icon: 'laptop',
  },
  {
    id: 'wearables',
    label: 'Wearables & Gadgets',
    description: 'Smartwatches και gadgets',
    icon: 'wearables',
  },
  {
    id: 'smarthome',
    label: 'Smarthome & Δικτυακά',
    description: 'Έξυπνο σπίτι και δικτυακός εξοπλισμός',
    icon: 'smarthome',
  },
  {
    id: 'all',
    label: 'Όλα τα παραπάνω',
    description: 'Ενημέρωση για όλες τις κατηγορίες',
    icon: 'all',
  },
];

/**
 * Mailchimp interest-group codes per category.
 *
 * Mirrors the legacy footer form exactly (legacy field name {@code group[14677][N]}):
 *   SmartPhones=1, TVs=2, Laptops=4, Tablets=16, Gaming=32, Wearables & Gadgets=8+64,
 *   Smarthome & Δικτυακά=128.
 *
 * Legacy reference:
 *   as-is/legacy/.../SiteBuilder/202506301000_GPLAT1090_Accessibility_Evaluation_At_Header_And_Footer.xml
 *   (the {@code footer-pop-categories} block)
 *
 * The Mailchimp audience/group id (14677) is fixed by the legacy list configuration.
 * 'all' is never submitted directly — the "Όλα τα παραπάνω" toggle expands to every
 * individual category before submission.
 */
export const MAILCHIMP_GROUP_ID = '14677';

export const MAILCHIMP_CATEGORY_GROUPS: Record<NewsletterCategory, number[]> = {
  smartphones: [1],
  tvs: [2],
  laptops: [4],
  tablets: [16],
  gaming: [32],
  wearables: [8, 64],
  smarthome: [128],
  all: [],
};

/**
 * Hardcoded fallback for the footer newsletter dropdown.
 *
 * Used when the backend (admin-managed) categories are unavailable or empty, so the
 * public footer always works. Mirrors the legacy footer interest-group block 1:1
 * (group id 14677, in the legacy render order) — see
 *   as-is/legacy/.../SiteBuilder/202506301000_GPLAT1090_Accessibility_Evaluation_At_Header_And_Footer.xml
 */
export const FALLBACK_FOOTER_CATEGORIES: FooterNewsletterCategory[] = [
  { id: 'smartphones', label: 'SmartPhones', groupId: MAILCHIMP_GROUP_ID, codes: [1] },
  { id: 'tvs', label: 'TVs', groupId: MAILCHIMP_GROUP_ID, codes: [2] },
  { id: 'tablets', label: 'Tablets', groupId: MAILCHIMP_GROUP_ID, codes: [16] },
  { id: 'gaming', label: 'Gaming', groupId: MAILCHIMP_GROUP_ID, codes: [32] },
  { id: 'laptops', label: 'Laptops', groupId: MAILCHIMP_GROUP_ID, codes: [4] },
  { id: 'wearables', label: 'Wearables & Gadgets', groupId: MAILCHIMP_GROUP_ID, codes: [8, 64] },
  { id: 'smarthome', label: 'Smarthome & Δικτυακά', groupId: MAILCHIMP_GROUP_ID, codes: [128] },
  { id: 'home-appliances', label: 'Οικιακές Μικροσυσκευές', groupId: MAILCHIMP_GROUP_ID, codes: [16384] },
];

/** Default form data */
export const DEFAULT_FORM_DATA: NewsletterFormData = {
  email: '',
  confirmEmail: '',
  firstName: '',
  lastName: '',
  categories: [],
  termsAccepted: false,
};

/** Footer form content */
export const FOOTER_FORM_CONTENT = {
  title: 'Εγγραφείτε στο Newsletter',
  placeholder: 'Εισάγετε το email σας...',
  button: 'Εγγραφή',
  benefits: ['Αποκλειστικές προσφορές', 'Νέα προϊόντα'],
};

/**
 * Footer newsletter status messages (legacy parity).
 *
 * Mirrors the legacy footer message spans (emailFormatError / successSubscriptionMsg /
 * errorSubscriptionMsg / userExistsMsg / selectCategoriesMsg) from
 * mailChimpNewLetter.js + the footer textBlock.
 */
export const FOOTER_NEWSLETTER_MESSAGES = {
  emailFormat: 'Καταχώρησες λάθος email, δοκίμασε ξανά!',
  selectCategories: 'Επίλεξε τουλάχιστον μία κατηγορία!',
  success: 'Ευχαριστούμε! Δες το email σου και επιβεβαίωσε την εγγραφή σου!',
  exists: 'Αυτό το email υπάρχει ήδη καταχωρημένο!',
  error: 'Ουπς! Κάτι πήγε λάθος! Προσπάθησε ξανά!',
} as const;

/** Modal content */
export const MODAL_CONTENT = {
  title: 'Newsletter',
  steps: {
    step1: {
      title: 'Επιλέξτε τα ενδιαφέροντά σας',
      subtitle: 'Τι θα θέλατε να λαμβάνετε;',
      emailLabel: 'Email',
      emailPlaceholder: 'you@example.com',
      categoriesLabel: 'Κατηγορίες ενδιαφέροντος',
      nextButton: 'Επόμενο',
    },
    step2: {
      title: 'Τα στοιχεία σας',
      subtitle: 'Σχεδόν έτοιμοι!',
      firstNameLabel: 'Όνομα',
      firstNamePlaceholder: 'Γιάννης',
      lastNameLabel: 'Επώνυμο',
      lastNamePlaceholder: 'Παπαδόπουλος',
      confirmEmailLabel: 'Επιβεβαίωση Email',
      confirmEmailPlaceholder: 'Επαναλάβετε το email σας',
      termsLabel: 'Αποδέχομαι τους',
      termsLink: 'Όρους Χρήσης',
      termsUrl: '/oroi-xrisis',
      privacyLink: 'Πολιτική Απορρήτου',
      privacyUrl: '/politiki-aporritou',
      backButton: 'Πίσω',
      submitButton: 'Εγγραφή',
    },
    step3: {
      title: 'Επιτυχία!',
      subtitle: 'Ευχαριστούμε για την εγγραφή σας',
      message: 'Σας έχει σταλεί email επιβεβαίωσης. Παρακαλούμε ελέγξτε τα εισερχόμενά σας και κάντε κλικ στον σύνδεσμο για να ολοκληρώσετε την εγγραφή σας.',
      checkSpam: 'Αν δεν το βρείτε, ελέγξτε τον φάκελο spam.',
      closeButton: 'Κλείσιμο',
    },
  },
  alreadySubscribed: {
    title: 'Είστε ήδη εγγεγραμμένοι!',
    message: 'Το email {email} είναι ήδη εγγεγραμμένο στο newsletter μας.',
    closeButton: 'Κλείσιμο',
  },
  pendingVerification: {
    title: 'Αναμονή επιβεβαίωσης',
    message: 'Έχετε ήδη αιτηθεί εγγραφή με αυτό το email. Παρακαλούμε ελέγξτε τα εισερχόμενά σας για το email επιβεβαίωσης.',
    closeButton: 'Κλείσιμο',
  },
};

/** Validation messages */
export const VALIDATION_MESSAGES = {
  emailRequired: 'Το email είναι υποχρεωτικό',
  emailInvalid: 'Παρακαλούμε εισάγετε ένα έγκυρο email',
  emailMismatch: 'Τα email δεν ταιριάζουν',
  firstNameRequired: 'Το όνομα είναι υποχρεωτικό',
  firstNameMinLength: 'Το όνομα πρέπει να έχει τουλάχιστον 2 χαρακτήρες',
  lastNameRequired: 'Το επώνυμο είναι υποχρεωτικό',
  lastNameMinLength: 'Το επώνυμο πρέπει να έχει τουλάχιστον 2 χαρακτήρες',
  categoriesRequired: 'Παρακαλούμε επιλέξτε τουλάχιστον μία κατηγορία',
  termsRequired: 'Πρέπει να αποδεχτείτε τους όρους χρήσης',
};

/** API endpoints */
export const API_ENDPOINTS = {
  subscribe: '/api/newsletter/subscribe',
  verify: '/api/newsletter/verify',
};
